import React, { useContext, useState, useEffect } from "react";
import HeaderAndFilters from "../components/HeadersAndFilters";
import AddNewLesson from "../components/modals/AddNewLesson";
import ListItems from "../components/ListItems";
import { OrganizationContext } from "../contexts/OrganizationContextProvider";
import { getAllLessonsWithParams, deleteLesson } from "../api/Api";
import { showToast } from "../utils/toastUtils";
import { ProjectContext } from "../contexts/ProjectContextProvider";
import { MdOutlineHub } from "react-icons/md";
import styles from "./Lesson.module.css";
import UpdateLessonModel from "../components/modals/UpdateLessonModel";
import Box from "../components/ui/Box";
import BreadCrumb from "../components/BreadCrumb";
import { TbBulb } from "react-icons/tb";

function Lesson() {
  const { data: organizationData } = useContext(OrganizationContext);
  const { data: projectData } = useContext(ProjectContext);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddLessonModal, setShowAddLessonModal] = useState(false);
  const [showUpdateLessonModal, setShowUpdateLessonModal] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [filteredLessons, setFilteredLessons] = useState([]);
  const [filters, setFilters] = useState({});

  const organizationId = organizationData?.current_organization?.id;

  const projectOptions =
    projectData?.map((project) => ({
      name: project?.name, // Display name in filter dropdown
      id: project?.id, // Use id as the value to identify the selected option
    })) || [];

  useEffect(() => {
    const fetchLessons = async () => {
      try {
        //  Get all lessons to extract full "Created By" options
        const allLessonsResponse = await getAllLessonsWithParams(
          organizationId,
          null,
          null
        );

        if (allLessonsResponse.status !== 200) {
          showToast(
            allLessonsResponse.message || "Failed to fetch lessons",
            "error"
          );
          return;
        }

        // Extract all unique users from all lessons
        const uniqueCreatedBy = [
          ...new Set(
            allLessonsResponse.data.map((lesson) =>
              JSON.stringify(lesson.created_by)
            )
          ),
        ];
        const createdByOptions = uniqueCreatedBy.map((userStr) => {
          const user = JSON.parse(userStr);
          return {
            name: `${user.first_name} ${user.last_name}`,
            id: user.id,
          };
        });

        // Fetch lessons filtered by user/project
        const filteredResponse = await getAllLessonsWithParams(
          organizationId,
          null,
          filters.user,
          filters.project
        );

        if (filteredResponse.status === 200) {
          setLessons(filteredResponse.data);
          setFilters((prevFilters) => ({
            ...prevFilters,
            createdByOptions,
          }));
        } else {
          showToast(
            filteredResponse.message || "Failed to fetch filtered lessons",
            "error"
          );
        }
      } catch (error) {
        console.error("Error fetching lessons:", error);
        showToast("Failed to fetch lessons", "error");
        setError("Failed to fetch lessons. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    if (organizationId) {
      fetchLessons();
    }
  }, [organizationId, filters.user, filters.project]);

  useEffect(() => {
    if (!filters.search) {
      setFilteredLessons(lessons);
      return;
    }

    const filtered = lessons.filter((lesson) => {
      const title = lesson.title?.toLowerCase() || "";
      const description = lesson.description?.toLowerCase() || "";
      return (
        title.includes(filters.search.toLowerCase()) ||
        description.includes(filters.search.toLowerCase())
      );
    });

    setFilteredLessons(filtered);
  }, [filters.search, lessons]);

  const handleDelete = async (lessonId) => {
    try {
      const response = await deleteLesson(lessonId);
      if (response.status === 200) {
        showToast("Lesson deleted successfully", "success");
        setLessons((prevLessons) =>
          prevLessons.filter((lesson) => lesson.id !== lessonId)
        );
      } else {
        showToast(response.message || "Failed to delete lesson", "error");
      }
    } catch (error) {
      showToast("Failed to delete lesson", "error");
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters((prevFilters) => ({ ...prevFilters, ...newFilters }));
  };

  const handleAddLesson = (newLesson) => {
    setLessons((prevLessons) => [...prevLessons, newLesson]);
  };

  const handleUpdateLesson = (updatedLesson) => {
    setLessons((prevLessons) =>
      prevLessons.map((lesson) =>
        lesson.id === updatedLesson.id ? updatedLesson : lesson
      )
    );
  };

  const openAddLessonModal = () => setShowAddLessonModal(true);
  const closeAddLessonModal = () => setShowAddLessonModal(false);

  const openUpdateLessonModal = (lesson) => {
    setSelectedLesson(lesson);
    setShowUpdateLessonModal(true);
  };
  const closeUpdateLessonModal = () => {
    setSelectedLesson(null);
    setShowUpdateLessonModal(false);
  };

  const breadcrumbArray = [
    { title: "Learning Hub", link: "/learning-hub" },
    { title: "Lessons", link: "/lessons" },
  ];

  return (
    <Box>
      <BreadCrumb
        className={styles.breadCrumb}
        array={breadcrumbArray}
        icon={<MdOutlineHub size={24} />}
      />
      <Box className="bg-white">
        <HeaderAndFilters
          filters={filters}
          showHeader={true}
          headerIcon={<TbBulb size={28} />}
          headerTitle="Lessons List"
          projectOptions={projectOptions}
          workstreamOptions={[
            { label: "Workstream 1", value: 1 },
            { label: "Workstream 2", value: 2 },
          ]}
          createdByOptions={filters.createdByOptions || []}
          handleClick={openAddLessonModal}
          onFilterChange={handleFilterChange}
          newButtonText="NEW LESSON"
          searchPlaceholder="Search..."
          showProjectButton={true}
          showWorkstreamButton={false}
          showCreatedByButton={true}
        />

        <ListItems
          items={filteredLessons}
          onDelete={handleDelete}
          onEdit={openUpdateLessonModal}
          titleField="title"
          descriptionField="description"
          dateField="created_at"
          avatarField="created_by"
          noItemsMessage='No lessons to display. To add a new lesson, click on the "NEW LESSON" button.'
        />
      </Box>

      <AddNewLesson
        show={showAddLessonModal}
        handleClose={closeAddLessonModal}
        onAddLesson={handleAddLesson}
      />
      {selectedLesson && (
        <UpdateLessonModel
          show={showUpdateLessonModal}
          handleClose={closeUpdateLessonModal}
          lesson={selectedLesson}
          onUpdateLesson={handleUpdateLesson}
        />
      )}
    </Box>
  );
}

export default Lesson;
